/**
 * Which blocks the Wins page draws, from three reads: the wins, the connectors (the same groups Home
 * shows, `homeConnectorGroups`) and the routines the connected apps' kits installed.
 *
 *   wins + connectors        → the gallery
 *   wins, no connectors      → the gallery, then the recommended connectors
 *   no wins + connectors     → the recommended routines, then the recommended connectors
 *   no wins, no connectors   → the recommended connectors
 *
 * Wins are produced by agents working through connected tools, so with no wins the page points at
 * what produces one: a tool to connect, or a routine to switch on.
 *
 * Never an empty state while a read that decides it is in flight, and never one after the wins read
 * failed — that is the error state with Retry. A gallery does not wait for the connectors read: the
 * recommendation under it simply appears when it lands.
 *
 * Shared 1:1 with iOS `WinsPageShape` and Android `winsPageShape`; the three test tables are the same
 * cases.
 */

export type WinsRead = 'loading' | 'error' | { count: number };
/** `unavailable` covers a failed read and an account with connectors switched off: nothing to offer. */
export type WinsConnectorsRead = 'loading' | 'unavailable' | { connected: number; recommended: number };
/** How many routine rows the connected apps offer (running or switchable on). */
export type WinsRoutinesRead = 'loading' | { rows: number };

/** `first` when nothing is connected yet (the line promises the first win), `more` otherwise. */
export type WinsConnectorsLine = 'first' | 'more';

export type WinsPageShape =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'wins'; connectors: boolean }
  | { kind: 'empty'; routines: boolean; connectors: WinsConnectorsLine | null };

export function winsPageShape(wins: WinsRead, connectors: WinsConnectorsRead, routines: WinsRoutinesRead): WinsPageShape {
  if (wins === 'loading') return { kind: 'loading' };
  if (wins === 'error') return { kind: 'error' };

  if (wins.count > 0) {
    const offer = typeof connectors === 'object' && connectors.connected === 0 && connectors.recommended > 0;
    return { kind: 'wins', connectors: offer };
  }

  if (connectors === 'loading') return { kind: 'loading' };
  if (connectors === 'unavailable') return { kind: 'empty', routines: false, connectors: null };

  const connectorsLine: WinsConnectorsLine | null =
    connectors.recommended === 0 ? null : connectors.connected === 0 ? 'first' : 'more';
  if (connectors.connected === 0) return { kind: 'empty', routines: false, connectors: connectorsLine };

  if (routines === 'loading') return { kind: 'loading' };
  return { kind: 'empty', routines: routines.rows > 0, connectors: connectorsLine };
}

/** How many recommended connectors the page offers — the same three Home's section shows. */
export const WINS_RECOMMENDED_CONNECTORS = 3;
